/**
 * @engine/vfx — Slang compile service seam.
 *
 * The VFX codegen emits Slang modules; turning them into WGSL requires a
 * toolchain the vfx package doesn't own. Hosts inject one:
 *
 * - editor: adapter over `getSlangCompiler()` (slang-wasm worker, slangc IPC
 *   fallback) — see `editor/src/assetTypes/vfx/handler.ts`
 * - tests / CI / node scripts: slangc CLI adapter over `tools/slang/<rid>/`
 *
 * The generated modules import `vfx.common` / `vfx.noise` / `vfx.emit`, so the
 * injected compiler must resolve those (worker VFS `/vfx/*.slang`, or
 * `-I packages/vfx/shaders/modules` for the CLI).
 */

export interface VFXSlangDiagnostic {
  readonly severity: 'error' | 'warning';
  readonly message: string;
  readonly line: number;
  readonly col: number;
}

export interface VFXSlangCompileResult {
  readonly ok: boolean;
  /** Emitted WGSL (when ok). */
  readonly wgsl?: string;
  readonly diagnostics: readonly VFXSlangDiagnostic[];
  readonly error?: string;
}

export type VFXSlangCompileFn = (
  source: string,
  opts: { moduleName: string }
) => Promise<VFXSlangCompileResult>;

let compileFn: VFXSlangCompileFn | null = null;

/** Inject the host's Slang→WGSL compiler (editor boot / test setup). */
export function setVFXSlangCompiler(fn: VFXSlangCompileFn): void {
  compileFn = fn;
}

export function hasVFXSlangCompiler(): boolean {
  return compileFn !== null;
}

/** Get the injected compiler; throws a setup hint if the host forgot to. */
export function getVFXSlangCompiler(): VFXSlangCompileFn {
  if (!compileFn) {
    throw new Error(
      '[VFX] No Slang compiler injected. Call setVFXSlangCompiler(...) before compiling VFX graphs ' +
      '(editor: adapter over getSlangCompiler(); tests: slangc CLI adapter).'
    );
  }
  return compileFn;
}

/** Format diagnostics into a single readable error message. */
export function formatSlangDiagnostics(result: VFXSlangCompileResult): string {
  if (result.diagnostics.length === 0) return result.error ?? 'unknown Slang compile error';
  return result.diagnostics
    .map(d => `${d.severity}${d.line > 0 ? ` [${d.line}:${d.col}]` : ''}: ${d.message}`)
    .join('\n');
}
